import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, Link } from '@mui/material';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <Box component="footer" sx={{ backgroundColor: 'white', position: 'relative' }}>
      {/* Decorative coffee seeds/leaves image */}
      <Box
        component="img"
        src="https://tostocoffee.com/images/coffee-seeds-leaves.png"
        alt=""
        sx={{ position: 'absolute', top: -128, right: 0, width: '230px' }}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Top row: logo and hashtag */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: { xs: 2, md: 0 },
          }}
        >
          <Box>
            <Link href="https://tostocoffee.com" sx={{ display: 'inline-flex' }}>
              <Box
                component="img"
                src="https://tostocoffee.com/images/logo.svg"
                alt="Logo"
                sx={{ height: '64px' }}
              />
            </Link>
          </Box>
          <Box>
            <Box sx={{ backgroundColor: '#009F9A', px: 5, py: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Hey August", cursive',
                  fontSize: '1.875rem',
                  textTransform: 'uppercase',
                  color: 'white',
                }}
              >
                {t('footer.hashtag')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Middle row: copyright, contact, socials */}
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: { xs: 2, md: 0 },
          }}
        >
          <Typography sx={{ fontSize: '1.25rem', textTransform: 'uppercase' }}>
            {t('footer.copyright', { year: currentYear })}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 2, md: 3 },
              fontSize: '1.25rem',
            }}
          >
            <Box>{t('footer.contact.email')}</Box>
            <Box>{t('footer.contact.phone')}</Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              {/* Facebook */}
              <Box component="span">
                <svg className="w-[35px]" width="35" height="35" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M32.6911 5.55762C17.7077 5.55762 5.44849 17.7896 5.44849 32.8548C5.44849 46.4761 15.4193 57.7818 28.4413 59.825V40.7551H21.5217V32.8548H28.4413V26.8341C28.4413 19.9962 32.5004 16.2367 38.739 16.2367C41.7085 16.2367 44.8141 16.7543 44.8141 16.7543V23.4833H41.3815C38.0035 23.4833 36.941 25.581 36.941 27.7331V32.8548H44.5145L43.2885 40.7551H36.941V59.825C43.3606 58.8111 49.2062 55.5357 53.4226 50.5899C57.639 45.6441 59.9484 39.3539 59.9338 32.8548C59.9338 17.7896 47.6746 5.55762 32.6911 5.55762Z" fill="#009F9A"></path>
                </svg>
              </Box>
              {/* Instagram */}
              <Box component="span">
                <svg className="w-[35px]" width="35" height="35" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.5786 5.44824H44.4624C53.18 5.44824 60.2631 12.5313 60.2631 21.249V44.1328C60.2631 48.3234 58.5984 52.3424 55.6352 55.3056C52.672 58.2688 48.653 59.9335 44.4624 59.9335H21.5786C12.8609 59.9335 5.77783 52.8505 5.77783 44.1328V21.249C5.77783 17.0584 7.44255 13.0394 10.4058 10.0762C13.369 7.11296 17.388 5.44824 21.5786 5.44824ZM21.0337 10.8968C18.4326 10.8968 15.9381 11.93 14.0989 13.7693C12.2596 15.6085 11.2264 18.1031 11.2264 20.7041V44.6777C11.2264 50.0989 15.6124 54.485 21.0337 54.485H45.0073C47.6083 54.485 50.1029 53.4517 51.9421 51.6125C53.7813 49.7733 54.8146 47.2787 54.8146 44.6777V20.7041C54.8146 15.2828 50.4285 10.8968 45.0073 10.8968H21.0337ZM47.3229 14.9832C48.226 14.9832 49.0922 15.3419 49.7308 15.9806C50.3694 16.6192 50.7282 17.4854 50.7282 18.3885C50.7282 19.2917 50.3694 20.1578 49.7308 20.7964C49.0922 21.4351 48.226 21.7938 47.3229 21.7938C46.4197 21.7938 45.5536 21.4351 44.9149 20.7964C44.2763 20.1578 43.9175 19.2917 43.9175 18.3885C43.9175 17.4854 44.2763 16.6192 44.9149 15.9806C45.5536 15.3419 46.4197 14.9832 47.3229 14.9832ZM33.0205 19.0696C36.6331 19.0696 40.0977 20.5047 42.6522 23.0592C45.2067 25.6137 46.6418 29.0783 46.6418 32.6909C46.6418 36.3035 45.2067 39.7681 42.6522 42.3226C40.0977 44.8771 36.6331 46.3122 33.0205 46.3122C29.4079 46.3122 25.9432 44.8771 23.3888 42.3226C20.8343 39.7681 19.3992 36.3035 19.3992 32.6909C19.3992 29.0783 20.8343 25.6137 23.3888 23.0592C25.9432 20.5047 29.4079 19.0696 33.0205 19.0696ZM33.0205 24.5181C30.8529 24.5181 28.7741 25.3792 27.2414 26.9119C25.7087 28.4446 24.8477 30.5233 24.8477 32.6909C24.8477 34.8585 25.7087 36.9372 27.2414 38.4699C28.7741 40.0026 30.8529 40.8637 33.0205 40.8637C35.188 40.8637 37.2668 40.0026 38.7995 38.4699C40.3322 36.9372 41.1933 34.8585 41.1933 32.6909C41.1933 30.5233 40.3322 28.4446 38.7995 26.9119C37.2668 25.3792 35.188 24.5181 33.0205 24.5181Z" fill="#009F9A"></path>
                </svg>
              </Box>
              {/* WhatsApp */}
              <Box component="span">
                <svg className="w-[35px]" width="35" height="35" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M32.8178 5.44824C17.9433 5.44824 5.82036 17.5712 5.82036 32.4457C5.82036 37.2132 7.07352 41.8444 9.41639 45.9308L5.60242 59.9335L19.9048 56.1741C23.855 58.3262 28.2955 59.4704 32.8178 59.4704C47.6923 59.4704 59.8153 47.3474 59.8153 32.473C59.8153 25.2537 57.0093 18.4702 51.9149 13.3759C46.8206 8.25424 40.0371 5.44824 32.8178 5.44824ZM32.8451 9.99777C38.8385 9.99777 44.4504 12.3406 48.7003 16.5905C52.9229 20.8403 55.2658 26.4796 55.2658 32.473C55.2658 44.8411 45.186 54.8937 32.8178 54.8937C28.7859 54.8937 24.8357 53.8312 21.4032 51.7608L20.5859 51.2976L12.0862 53.5315L14.3473 45.2498L13.8025 44.378C11.5686 40.8637 10.3699 36.6956 10.3699 32.4457C10.3971 20.0775 20.4497 9.99777 32.8451 9.99777ZM23.2557 19.9686C22.8198 19.9686 22.0842 20.132 21.4576 20.8131C20.8583 21.4942 19.0875 23.156 19.0875 26.4523C19.0875 29.7759 21.5121 32.9633 21.8118 33.4264C22.1932 33.8896 26.6065 40.7002 33.3899 43.588C34.9972 44.3235 36.2504 44.7322 37.2311 45.0318C38.8385 45.5494 40.3096 45.4677 41.481 45.3042C42.7886 45.1135 45.4584 43.6697 46.0305 42.0896C46.6026 40.5095 46.6026 39.1746 46.4392 38.875C46.2485 38.6026 45.8126 38.4391 45.1315 38.1394C44.4504 37.758 41.1268 36.1235 40.5275 35.9055C39.9009 35.6876 39.5195 35.5786 39.0019 36.2324C38.566 36.9135 37.2584 38.4391 36.877 38.875C36.4683 39.3381 36.0869 39.3926 35.4331 39.0657C34.7248 38.7115 32.5454 38.0032 29.9846 35.7148C27.9686 33.9168 26.6337 31.7102 26.2251 31.0291C25.8982 30.3753 26.1979 29.9666 26.5248 29.667C26.8244 29.3673 27.2603 28.8769 27.5328 28.4683C27.8869 28.0869 27.9959 27.7872 28.2138 27.3513C28.4318 26.8882 28.3228 26.5068 28.1593 26.1799C27.9959 25.8802 26.6337 22.5021 26.0617 21.1673C25.5168 19.8596 24.9719 20.0231 24.5361 19.9958C24.1547 19.9958 23.7188 19.9686 23.2557 19.9686Z" fill="#009F9A"></path>
                </svg>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ my: 5, borderTop: '1px solid #E5E7EB' }} />

        {/* Language selector (optional external links removed; header switcher controls language) */}
      </Container>
    </Box>
  );
};

export default Footer; 